import { useEffect, useMemo, useState } from 'react';
import { Seo } from '../components/Seo';
import { api } from '../lib/api';
import { formatPriceK } from '../lib/price';
import type { MenuResponse } from '../lib/types';

type TabletTab = 'guide' | 'cocktail' | 'whisky' | 'others';
type GuideTab = 'guide_info' | 'notice' | 'delivery';
type WhiskyTab = 'scotch' | 'american' | 'other_countries';
type CocktailTab = 'signature' | 'classic' | 'custom';
type OthersTab = 'wine' | 'other_liquor';

const TABLET_TABS: Array<{ id: TabletTab; label: string }> = [
  { id: 'guide', label: '이용안내' },
  { id: 'cocktail', label: '칵테일' },
  { id: 'whisky', label: '위스키' },
  { id: 'others', label: '기타 주류' }
];

const GUIDE_TABS: Array<{ id: GuideTab; label: string }> = [
  { id: 'guide_info', label: '이용안내' },
  { id: 'notice', label: '공지사항' },
  { id: 'delivery', label: '배달추천' }
];

const WHISKY_TABS: Array<{ id: WhiskyTab; label: string }> = [
  { id: 'scotch', label: '스카치' },
  { id: 'american', label: '아메리칸' },
  { id: 'other_countries', label: '기타 국가' }
];

const COCKTAIL_TABS: Array<{ id: CocktailTab; label: string }> = [
  { id: 'signature', label: '타로 시그니처' },
  { id: 'classic', label: '칵테일' },
  { id: 'custom', label: '커스텀 칵테일' }
];

const OTHERS_TABS: Array<{ id: OthersTab; label: string }> = [
  { id: 'wine', label: '와인' },
  { id: 'other_liquor', label: '기타 주류' }
];

export function TabletMenuPage() {
  const [data, setData] = useState<MenuResponse | null>(null);
  const [activeTab, setActiveTab] = useState<TabletTab>('guide');
  const [activeGuideTab, setActiveGuideTab] = useState<GuideTab>('guide_info');
  const [activeWhiskyTab, setActiveWhiskyTab] = useState<WhiskyTab>('scotch');
  const [activeCocktailTab, setActiveCocktailTab] = useState<CocktailTab>('signature');
  const [activeOthersTab, setActiveOthersTab] = useState<OthersTab>('wine');

  useEffect(() => {
    api.getPublicMenu('tablet').then((res) => {
      setData(res);
    });
  }, []);

  const grouped = useMemo(() => {
    if (!data) {
      return {
        cocktail: [],
        whisky: [],
        others: []
      } as const;
    }

    const allItems = data.categories.flatMap((category) =>
      category.items.map((item) => ({
        ...item,
        category_name: category.name
      }))
    );

    const cocktail = allItems.filter((item) => item.source === 'cocktail');
    const spirits = allItems.filter((item) => item.source === 'spirits');
    const whisky = spirits;
    const others = spirits;

    return { cocktail, whisky, others } as const;
  }, [data]);

  function categoryEquals(categoryName: string, target: string): boolean {
    return categoryName.trim().toLowerCase() === target.trim().toLowerCase();
  }

  const guideItems = useMemo(() => {
    if (!data) return [];
    return data.banners.filter((banner) => {
      const category = (banner.category ?? '').trim().toLowerCase();
      if (activeGuideTab === 'guide_info') {
        return category === '' || category.includes('이용안내') || category.includes('guide');
      }
      if (activeGuideTab === 'notice') {
        return category.includes('공지') || category.includes('notice');
      }
      return category.includes('배달') || category.includes('delivery');
    });
  }, [activeGuideTab, data]);

  const whiskyItems = useMemo(() => {
    if (activeWhiskyTab === 'scotch') {
      return grouped.whisky.filter((item) => categoryEquals(item.category_name, 'Scotch'));
    }
    if (activeWhiskyTab === 'american') {
      return grouped.whisky.filter((item) => categoryEquals(item.category_name, 'American'));
    }
    return grouped.whisky.filter(
      (item) => categoryEquals(item.category_name, 'Other Country') || categoryEquals(item.category_name, 'Others')
    );
  }, [activeWhiskyTab, grouped.whisky]);

  const cocktailItems = useMemo(() => {
    if (activeCocktailTab === 'signature') {
      return grouped.cocktail.filter((item) => categoryEquals(item.category_name, 'Signature'));
    }
    if (activeCocktailTab === 'classic') {
      return grouped.cocktail.filter((item) => categoryEquals(item.category_name, 'Classic'));
    }
    return [];
  }, [activeCocktailTab, grouped.cocktail]);

  const othersItems = useMemo(() => {
    if (activeOthersTab === 'wine') {
      return grouped.others.filter((item) => categoryEquals(item.category_name, 'Wine'));
    }
    return grouped.others.filter((item) => categoryEquals(item.category_name, 'Others'));
  }, [activeOthersTab, grouped.others]);

  return (
    <section className="tablet-menu-page">
      <Seo title="타로맨스 | Tablet Menu" description="태블릿 전용 메뉴" />
      {!data && <p>메뉴를 불러오는 중...</p>}

      {data && (
        <div className={activeTab === 'guide' || activeTab === 'whisky' || activeTab === 'cocktail' || activeTab === 'others' ? 'tablet-menu-shell with-sub' : 'tablet-menu-shell'}>
          <aside className="tablet-category-rail" aria-label="카테고리">
            {TABLET_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={tab.id === activeTab ? 'tablet-category active' : 'tablet-category'}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </aside>

          {(activeTab === 'guide' || activeTab === 'whisky' || activeTab === 'cocktail' || activeTab === 'others') && (
            <aside className="tablet-sub-rail" aria-label="세부 카테고리">
              {GUIDE_TABS.map((tab) => (
                activeTab === 'guide' ? (
                  <button
                    key={tab.id}
                    type="button"
                    className={tab.id === activeGuideTab ? 'guide-subtab active' : 'guide-subtab'}
                    onClick={() => setActiveGuideTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ) : null
              ))}

              {activeTab === 'whisky' &&
                WHISKY_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={tab.id === activeWhiskyTab ? 'guide-subtab active' : 'guide-subtab'}
                    onClick={() => setActiveWhiskyTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}

              {activeTab === 'cocktail' &&
                COCKTAIL_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={tab.id === activeCocktailTab ? 'guide-subtab active' : 'guide-subtab'}
                    onClick={() => setActiveCocktailTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}

              {activeTab === 'others' &&
                OTHERS_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={tab.id === activeOthersTab ? 'guide-subtab active' : 'guide-subtab'}
                    onClick={() => setActiveOthersTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
            </aside>
          )}

          <article className="tablet-scroll-board">
            <header className="tablet-scroll-head">
              <h1>{TABLET_TABS.find((tab) => tab.id === activeTab)?.label ?? 'MENU'}</h1>
              {activeTab === 'whisky' ? (
                <div className="tablet-whisky-head-cols" aria-hidden="true">
                  <span>30ml</span>
                  <span>Bottle</span>
                </div>
              ) : null}
            </header>

            <div className="tablet-scroll-list">
              {activeTab === 'guide' &&
                guideItems.map((banner) => (
                  <div key={banner.id} className="tablet-item-row guide">
                    <div>
                      <h3>{banner.title}</h3>
                      {banner.body ? <p>{banner.body}</p> : null}
                    </div>
                  </div>
                ))}

              {activeTab === 'guide' && guideItems.length === 0 && (
                <div className="tablet-item-row guide">
                  <div>
                    <h3>표시할 안내가 없습니다.</h3>
                  </div>
                </div>
              )}

              {activeTab === 'cocktail' &&
                cocktailItems.map((item) => (
                  item.is_spacer ? (
                    <div key={item.id} className="tablet-item-spacer" aria-hidden="true" />
                  ) : (
                    <div key={item.id} className={item.is_title ? 'tablet-item-row title-item' : 'tablet-item-row'}>
                      <div>
                        <h3>{item.name}</h3>
                        {item.description ? <p>{item.description}</p> : null}
                      </div>
                      <div className="tablet-price-col">
                        {item.is_title && item.price <= 0 ? null : (
                          <strong>{formatPriceK(item.price)}</strong>
                        )}
                      </div>
                    </div>
                  )
                ))}

              {activeTab === 'whisky' &&
                (
                  <>
                    {whiskyItems.map((item) => (
                  item.is_spacer ? (
                    <div key={item.id} className="tablet-item-spacer" aria-hidden="true" />
                  ) : (
                    (() => {
                      const soldOut30 = (item.soldout_30ml ?? item.is_soldout) || (item.price_30ml ?? 0) <= 0;
                      const soldOutBottle = (item.soldout_bottle ?? item.is_soldout) || (item.price_bottle ?? 0) <= 0;
                      const bothSoldOut = soldOut30 && soldOutBottle;

                      return (
                        <div key={item.id} className={item.is_title ? 'tablet-item-row whisky-row title-item' : 'tablet-item-row whisky-row'}>
                          <div>
                            <h3>{item.name}</h3>
                            {item.description ? <p>{item.description}</p> : null}
                          </div>

                          {item.is_title ? (
                            <>
                              <div className="tablet-price-col" />
                              <div className="tablet-price-col" />
                            </>
                          ) : bothSoldOut ? (
                            <div className="tablet-price-bridge" aria-label="SOLD OUT">
                              <span className="tablet-price-col" aria-hidden="true" />
                              <span className="tablet-price-col" aria-hidden="true" />
                              <span className="badge soldout soldout-large">SOLD OUT</span>
                            </div>
                          ) : (
                            <>
                              <div className="tablet-price-col">
                                {soldOut30 ? <span className="badge soldout">SOLD OUT</span> : <strong>{formatPriceK(item.price_30ml ?? 0)}</strong>}
                              </div>
                              <div className="tablet-price-col">
                                {soldOutBottle ? <span className="badge soldout">SOLD OUT</span> : <strong>{formatPriceK(item.price_bottle ?? 0)}</strong>}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()
                  )
                ))}
                  </>
                )}

              {activeTab === 'others' &&
                othersItems.map((item) => (
                  item.is_spacer ? (
                    <div key={item.id} className="tablet-item-spacer" aria-hidden="true" />
                  ) : (
                    (() => {
                      const soldOut30 = item.soldout_30ml ?? item.is_soldout;
                      const soldOutBottle = item.soldout_bottle ?? item.is_soldout;
                      const fullySoldOut = item.source === 'spirits' ? soldOut30 && soldOutBottle : item.is_soldout;

                      return (
                        <div key={item.id} className={item.is_title ? 'tablet-item-row title-item' : 'tablet-item-row'}>
                          <div>
                            <h3>{item.name}</h3>
                            {item.description ? <p>{item.description}</p> : null}
                          </div>
                          <div className="tablet-price-col">
                            {item.is_title ? null : fullySoldOut ? (
                              <span className="badge soldout">SOLD OUT</span>
                            ) : (
                              <strong>{formatPriceK(item.price)}</strong>
                            )}
                          </div>
                        </div>
                      );
                    })()
                  )
                ))}

              {activeTab !== 'guide' &&
                ((activeTab === 'cocktail' && cocktailItems.length === 0) ||
                  (activeTab === 'whisky' && whiskyItems.length === 0) ||
                  (activeTab === 'others' && othersItems.length === 0)) && (
                  <div className="tablet-item-row guide">
                    <div>
                      <h3>{activeTab === 'cocktail' && activeCocktailTab === 'custom' ? '커스텀 칵테일은 25,000 원 고정입니다.' : '표시할 메뉴가 없습니다.'}</h3>
                    </div>
                  </div>
                )}
            </div>
          </article>
        </div>
      )}
    </section>
  );
}
